import React from 'react';
import { useTheme } from '../context/ThemeContext';
import './AuthPages.scss';

const TermsOfServicePage = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <div className={`auth-page ${isDark ? 'auth-page--dark' : 'auth-page--light'}`}>
      <div className="policy-page">
        <div className="policy-page__content">
          <h1 className="policy-page__title">Terms of Service</h1>
          <p className="policy-page__updated"><strong>Last Updated:</strong> December 29, 2025</p>
          
          <p className="policy-page__intro">
            Welcome to ChillVerify. These Terms of Service ("Terms") govern your use of our temperature and humidity monitoring services, mobile application, and website (collectively, the "Services"). By accessing or using our Services, you agree to be bound by these Terms.
          </p>

          <section className="policy-page__section">
            <h2>1. Acceptance of Terms</h2>
            <p>By creating an account or using our Services, you acknowledge that you have read, understood, and agree to be bound by these Terms and our Privacy Policy. If you do not agree to these Terms, you may not use our Services.</p>
          </section>

          <section className="policy-page__section">
            <h2>2. Description of Services</h2>
            <p>ChillVerify provides:</p>
            <ul>
              <li>Temperature and humidity monitoring through connected sensors</li>
              <li>Real-time alerts and notifications for threshold violations</li>
              <li>Historical data tracking and reporting</li>
              <li>Zone management and trip tracking for mobile monitoring</li>
              <li>Multi-user account management for teams and organizations</li>
            </ul>
          </section>

          <section className="policy-page__section">
            <h2>3. Account Registration</h2>
            <p>To use our Services, you must:</p>
            <ul>
              <li>Be at least 18 years old or have parental consent</li>
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Notify us immediately of any unauthorized account access</li>
              <li>Accept responsibility for all activities under your account</li>
            </ul>
          </section>

          <section className="policy-page__section">
            <h2>4. Subscription and Payments</h2>
            <h3>4.1 Free Trial</h3>
            <p>New users may be eligible for a 14-day free trial. Trial terms may vary and are subject to change.</p>
            
            <h3>4.2 Subscription Plans</h3>
            <p>Paid subscriptions are billed monthly or annually as selected. Prices are subject to change with 30 days notice.</p>
            
            <h3>4.3 Payment Processing</h3>
            <p>Payments are processed securely through Stripe. By providing payment information, you authorize us to charge your payment method for subscription fees.</p>
            
            <h3>4.4 Refunds</h3>
            <p>Subscription fees are generally non-refundable. However, we may provide refunds at our discretion for technical issues or service failures.</p>
          </section>

          <section className="policy-page__section">
            <h2>5. User Responsibilities</h2>
            <p>You agree to:</p>
            <ul>
              <li>Use the Services only for lawful purposes</li>
              <li>Not attempt to reverse engineer or hack our systems</li>
              <li>Not share your account credentials with unauthorized users</li>
              <li>Properly install and maintain sensors according to guidelines</li>
              <li>Not use the Services to monitor individuals without their consent</li>
              <li>Comply with all applicable laws and regulations</li>
            </ul>
          </section>

          <section className="policy-page__section">
            <h2>6. Sensor Hardware</h2>
            <h3>6.1 Hardware Purchase</h3>
            <p>Sensors and gateways purchased through our store are subject to standard return policies. Defective hardware may be replaced within the warranty period.</p>
            
            <h3>6.2 Compatibility</h3>
            <p>We guarantee compatibility only with sensors purchased directly from ChillVerify or authorized partners.</p>
            
            <h3>6.3 Installation</h3>
            <p>Users are responsible for proper sensor installation. Improper installation may void warranties and affect data accuracy.</p>
          </section>

          <section className="policy-page__section">
            <h2>7. Data and Privacy</h2>
            <p>Your use of the Services is also governed by our Privacy Policy, which describes how we collect, use, and protect your data. By using our Services, you consent to our data practices as described in the Privacy Policy.</p>
          </section>

          <section className="policy-page__section">
            <h2>8. Service Availability</h2>
            <p>While we strive for 99.9% uptime, we do not guarantee uninterrupted service. We may temporarily suspend Services for:</p>
            <ul>
              <li>Scheduled maintenance (with advance notice when possible)</li>
              <li>Emergency repairs or security updates</li>
              <li>Circumstances beyond our reasonable control</li>
            </ul>
          </section>

          <section className="policy-page__section">
            <h2>9. Limitation of Liability</h2>
            <p>To the maximum extent permitted by law:</p>
            <ul>
              <li>Our Services are provided "as is" without warranties of any kind</li>
              <li>We are not liable for any indirect, incidental, or consequential damages</li>
              <li>Our total liability shall not exceed the amount paid by you in the 12 months preceding the claim</li>
              <li>We are not responsible for losses due to sensor malfunction, connectivity issues, or missed alerts</li>
            </ul>
          </section>

          <section className="policy-page__section">
            <h2>10. Indemnification</h2>
            <p>You agree to indemnify and hold harmless ChillVerify, its officers, directors, employees, and agents from any claims, damages, or expenses arising from your use of the Services or violation of these Terms.</p>
          </section>

          <section className="policy-page__section">
            <h2>11. Termination</h2>
            <p>Either party may terminate the account at any time. Upon termination:</p>
            <ul>
              <li>Your access to the Services will be immediately revoked</li>
              <li>Historical data may be deleted after 30 days</li>
              <li>No refunds will be provided for unused subscription time</li>
              <li>You may request an export of your data before termination</li>
            </ul>
          </section>

          <section className="policy-page__section">
            <h2>12. Modifications to Terms</h2>
            <p>We reserve the right to modify these Terms at any time. Material changes will be communicated via email or in-app notification at least 30 days before taking effect. Continued use of the Services after changes take effect constitutes acceptance of the new Terms.</p>
          </section>

          <section className="policy-page__section">
            <h2>13. Governing Law</h2>
            <p>These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to conflict of law principles. Any disputes shall be resolved in the courts of competent jurisdiction.</p>
          </section>

          <section className="policy-page__section">
            <h2>14. Contact Information</h2>
            <p>For questions about these Terms, please contact us:</p>
            <ul>
              <li><strong>Email:</strong> <a href="mailto:support@chillverify.com">support@chillverify.com</a></li>
              <li><strong>Website:</strong> <a href="https://chillverify.com" target="_blank" rel="noopener noreferrer">https://chillverify.com</a></li>
            </ul>
          </section>

          <section className="policy-page__section">
            <p><strong>By using ChillVerify, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.</strong></p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
